import type { OrderDetail } from '@repo/api-contracts';
import { trpc } from '@/shared/api';

interface InvalidateOrders {
  readonly list: () => Promise<void>;
  readonly detail: (orderId: string) => Promise<void>;
  /** 처리 응답으로 받은 최신 상세를 캐시에 넣는다 — 같은 상세를 다시 요청하지 않는다. */
  readonly putDetail: (order: OrderDetail) => void;
  /**
   * 주문 상태를 보여 주는 관리자 견적 화면(견적 상세의 주문 영역·견적 목록)을 다시 불러온다. 견적 id 를 모르는 처리(송장·반려)도
   * 있어 견적 상세 전부를 무효화한다 — 열려 있는 화면만 다시 요청하고 나머지는 다음에 열 때 읽는다.
   */
  readonly quotes: () => Promise<void>;
  /** 주문의 발주서 조회(저장본·기본값)를 다시 불러온다. */
  readonly productionSheet: (orderId: string) => Promise<void>;
}

/**
 * 어드민 주문 캐시 갱신 — 목록·상세 invalidation 과 상세 캐시 쓰기를 한곳에 모은다.
 * 무효화는 재조회 promise 를 돌려준다 — 처리 훅이 기다리면 새 상태가 오기 전에 버튼이 풀리지 않는다.
 */
export const useInvalidateOrders = (): InvalidateOrders => {
  const utils = trpc.useUtils();

  return {
    list: () => utils.orderManagement.list.invalidate(),
    detail: (orderId) => utils.orderManagement.get.invalidate({ orderId }),
    productionSheet: (orderId) =>
      utils.orderManagement.getProductionOrderSheet.invalidate({ orderId }),
    putDetail: (order) => {
      utils.orderManagement.get.setData({ orderId: order.id }, order);
    },
    quotes: async () => {
      await Promise.all([
        utils.quoteManagement.get.invalidate(),
        utils.quoteManagement.list.invalidate(),
      ]);
    },
  };
};
